import { configureStore } from "@reduxjs/toolkit";
import { Middleware, UnknownAction } from "redux";
import createSagaMiddleware from "redux-saga";

import { showNotification } from "@shared/store/actions";

import appSagas from "./saga";
import rootReducer from "./reducer";

const sagaMiddleware = createSagaMiddleware();

interface FailurePayload {
  error?: string;
  message?: string;
  code?: number;
}

const isPayloadAction = (action: unknown): action is UnknownAction & { payload?: FailurePayload } =>
  typeof action === "object" && action !== null && typeof (action as UnknownAction).type === "string";

const errorHandlerMiddleware: Middleware = () => (next) => (action) => {
  if (!isPayloadAction(action)) {
    return next(action);
  }

  const payload = action.payload;

  if (action.type.includes("FAILURE")) {
    next(
      showNotification({
        message: payload?.error || payload?.message || "",
        appearance: "error",
      }),
    );

    if (payload && (payload.code === 401 || payload.code === 403)) {
      localStorage.clear();
    }
  }

  if (action.type.includes("SUCCESS") && payload && payload.message) {
    next(
      showNotification({
        message: payload.message,
        appearance: "success",
      }),
    );
  }

  return next(action);
};

export default function createStore() {
  const store = configureStore({
    reducer: rootReducer(),
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({ thunk: false, serializableCheck: false }).concat(sagaMiddleware, errorHandlerMiddleware),
    devTools: import.meta.env.DEV,
  });
  sagaMiddleware.run(appSagas);

  return { store };
}
